import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import Layout from '../../Component/Layout';
import ReactDOM from 'react-dom';
import Select from 'react-select';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaTrash } from 'react-icons/fa';
import { MASTER_API } from '../../apiConfig';
import { hasPermission } from '../../utils/permission';

// ==========================================
// STYLING — identical to Routings.js
// ==========================================
const smallInput = { width: "100%", fontSize: "12px", padding: "4px 6px", height: "28px", boxSizing: "border-box", border: "1px solid #ccc", borderRadius: "4px" };
const smallLabel = { display: "block", fontSize: "13px", fontWeight: 600, marginBottom: 6, color: "#334155" };
const thStyle = { padding: 8, borderBottom: "1px solid #ccc", textAlign: "left", whiteSpace: "nowrap", color: "#334155" };
const tdStyle = { padding: 8, borderBottom: "1px solid #eee", whiteSpace: "nowrap" };

const customSelectStyles = {
    control: (base) => ({ ...base, minHeight: "28px", height: "28px", fontSize: "12px", borderColor: "#ccc", boxShadow: "none", '&:hover': { borderColor: "#b3b3b3" } }),
    valueContainer: (base) => ({ ...base, padding: "0 6px", display: "flex", alignItems: "center" }),
    input: (base) => ({ ...base, margin: 0, padding: 0, color: "#334155" }),
    indicatorsContainer: (base) => ({ ...base, height: "26px" }),
    dropdownIndicator: (base) => ({ ...base, padding: "2px 6px" }),
    clearIndicator: (base) => ({ ...base, padding: "2px" }),
    menu: (base) => ({ ...base, fontSize: "12px", zIndex: 99999 }),
    menuPortal: (base) => ({ ...base, zIndex: 99999 }),
    option: (base, state) => ({ ...base, backgroundColor: state.isSelected ? "#1e293b" : state.isFocused ? "#f1f5f9" : "white", color: state.isSelected ? "white" : "#334155", cursor: "pointer", fontSize: "12px", padding: "8px 12px" })
};

// Filter-page-only styling (kept from the original upload screen)
const filterSelectStyles = {
    control: (base) => ({ ...base, minHeight: 32, height: 32, fontSize: '13px', borderColor: '#dee2e6', boxShadow: 'none', '&:hover': { borderColor: '#b3b3b3' } }),
    valueContainer: (base) => ({ ...base, padding: '0 8px', height: '32px', display: 'flex', alignItems: 'center' }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    dropdownIndicator: (base) => ({ ...base, padding: 4 }),
    menu: (base) => ({ ...base, fontSize: '13px', zIndex: 9999 }),
    menuPortalTarget: (base) => ({ ...base, zIndex: 9999 }),
    option: (base, state) => ({ ...base, backgroundColor: state.isSelected ? '#1e293b' : state.isFocused ? '#f1f5f9' : 'white', color: state.isSelected ? 'white' : '#334155', cursor: 'pointer', fontSize: '13px' })
};

const styles = {
    compactLabel: { fontSize: '12px', fontWeight: 600, marginBottom: '2px', color: '#334155' },
    filterCard: { background: '#ffffff', borderRadius: 8, padding: 15, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 15 },
    pageTitleBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
    pageTitle: { fontSize: 20, fontWeight: 700, color: '#1e293b' },
    pageSubtitle: { fontSize: 12, color: '#64748b' },
    tableContainer: { background: '#ffffff', borderRadius: 8, padding: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' },
    erpTable: { width: '100%', borderCollapse: 'collapse' },
    erpTh: { background: '#f8fafc', fontSize: 12, fontWeight: 600, padding: '10px', borderBottom: '1px solid #e5e7eb', color: '#1e293b' },
    erpTd: { fontSize: 13, padding: '10px', borderBottom: '1px solid #f1f5f9', color: '#334155' }
};

const productTypeOptions = [
    { value: 'FINISHED GOODS', label: 'Finished Goods' },
    { value: 'SEMI FINISHED', label: 'Semi Finished' }
];

const toStrOptions = (arr) => (arr || []).map(v => ({ value: v, label: v }));

const emptyOperation = () => ({
    process_id: "", process_name: "",
    sub_process_id: "", sub_process_name: "",
    workcenterName: "", workcenter_description: "",
    activities: { setup_value: "", machine_value: "", labour_value: "", overhead_value: "" },
    processParams: {},
    machine_parameters: [],
    process_parameters: [],
    inspection_parameters: []
});

const isSfgComponent = (c) =>
    String(c.component_product).startsWith("8") ||
    (c.product_type && c.product_type.toLowerCase().includes("semi")) ||
    (c.product_type && c.product_type.toUpperCase() === "HALB");

function RoutingDataUpload() {
    const canCreate = hasPermission('MD_ROUTING_UPLOAD', 'create');
    const canView = hasPermission('MD_ROUTING_UPLOAD', 'view');
    const canEdit = hasPermission('MD_ROUTING_UPLOAD', 'edit');
    const canDelete = hasPermission('MD_ROUTING_UPLOAD', 'delete');

    // ── Upload-screen filters/table state (unchanged from before) ──
    const [filters, setFilters] = useState({
        productType: null, category: null, cableType: null, code: '', color: '',
        cores: null, crossSection: null, referenceOfStandard: null, voltage: null,
        construction: null, productCode: '', description: ''
    });
    const [filterOptions, setFilterOptions] = useState({
        category: [], cableType: [], code: [], color: [], noOfCores: [],
        nominalCrossSection: [], referenceOfStandard: [], voltage: [], construction: []
    });
    const [codeDescList, setCodeDescList] = useState([]);
    const [rows, setRows] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [rowPublishing, setRowPublishing] = useState({});
    const [pendingDrafts, setPendingDrafts] = useState([]);
    const [rowDraftListIds, setRowDraftListIds] = useState(null);
    const [showRowDraftsModal, setShowRowDraftsModal] = useState(false);
    const [showDraftsListModal, setShowDraftsListModal] = useState(false);
    const [publishingDraftId, setPublishingDraftId] = useState(null);
    const [deletingDraftId, setDeletingDraftId] = useState(null);
    const itemsPerPage = 10;

    // ── "Upload Routing File" → process-wise Download Template / Upload File modal ──
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploadProcesses, setUploadProcesses] = useState([]); // [{ process_id, process_name }]
    const [loadingUploadProcesses, setLoadingUploadProcesses] = useState(false);
    const [rowFiles, setRowFiles] = useState({});         // { [process_id]: File }
    const [rowStatus, setRowStatus] = useState({});        // { [process_id]: 'idle' | 'uploading' | 'success' | 'error' }
    const [rowDownloading, setRowDownloading] = useState({}); // { [process_id]: boolean }
    const [rowDrafts, setRowDrafts] = useState({});        // { [process_id]: [{ routing_id, routingCode, productCode, description }] }
    const [rowErrorMsg, setRowErrorMsg] = useState({});    // { [process_id]: string }
    const [replaceConfirm, setReplaceConfirm] = useState(null);

    const selectedType = (filters.productType?.value || '').trim().toLowerCase();
    const hasType = !!selectedType;
    const fieldBox = { minWidth: 105, flex: '1 1 105px' };

    // ── Full modal system — ported 1:1 from Routings.js ──
    const [products, setProducts] = useState([]);
    const [workcenters, setWorkcenters] = useState([]);
    const [plants, setPlants] = useState([]);
    const [routingHeaders, setRoutingHeaders] = useState([]);
    const [workcenterConfigs, setWorkcenterConfigs] = useState([]);
    const [bomAlternatives, setBomAlternatives] = useState([]);

    const [showModal, setShowModal] = useState(false);
    const [isViewing, setIsViewing] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formHeader, setFormHeader] = useState({ productCode: "", mainLocation: "", startDate: "", endDate: "", alternative_bom: "", baseUnit: "", uom: "", reference_routing: "", product_type: "" });
    const [operations, setOperations] = useState([emptyOperation()]);
    const [components, setComponents] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [activeTab, setActiveTab] = useState("components");
    const [routingLogs, setRoutingLogs] = useState([]);
    const [saving, setSaving] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [componentsReadOnly, setComponentsReadOnly] = useState(false);
    const [isDraftView, setIsDraftView] = useState(false);

    const [bomLevelStack, setBomLevelStack] = useState([]);
    // eslint-disable-next-line no-unused-vars
    const [currentBomChildren, setCurrentBomChildren] = useState([]);
    const visitedSfgRef = useRef({});
    const [sfgChoices, setSfgChoices] = useState([]);
    const sfgMapRef = useRef({});
    const [routingCache, setRoutingCache] = useState({});
    const productRoutingIdsRef = useRef({});
    const openedFromTreeRef = useRef(false);
    const openedFromUploadRef = useRef(false);
    const openedFromRowDraftsListRef = useRef(false);
    const isCreateModeRef = useRef(false); // always false here — this screen never creates
    const editingIdRef = useRef(null);
    const formHeaderRef = useRef(formHeader);

    useEffect(() => { editingIdRef.current = editingId; }, [editingId]);
    useEffect(() => { formHeaderRef.current = formHeader; }, [formHeader]);

    // ── Tree modal state — identical to Routings.js ──
    const [expandedNodes, setExpandedNodes] = useState({});
    const [showTreeModal, setShowTreeModal] = useState(false);
    const [routingTree, setRoutingTree] = useState(null);

    // ── Load reference data the modal's dropdowns need ──
    useEffect(() => {
        const fetchSafe = async (url, setter) => {
            try {
                const res = await axios.get(url);
                const data = Array.isArray(res.data) ? res.data : Array.isArray(res.data?.data) ? res.data.data : [];
                setter(data);
            } catch (err) {
                console.error(`Failed to load data from ${url}`, err);
                setter([]);
            }
        };
        fetchSafe(`${MASTER_API}/bom/products`, setProducts);
        fetchSafe(`${MASTER_API}/routings/workcenters`, setWorkcenters);
        fetchSafe(`${MASTER_API}/routings/plants`, setPlants);
        fetchSafe(`${MASTER_API}/routings/alternative-bom`, setBomAlternatives);
        fetchSafe(`${MASTER_API}/routings/workcenter-config`, setWorkcenterConfigs);
        fetchSafe(`${MASTER_API}/routings/headers`, setRoutingHeaders);
    }, []);

    const loadPendingDrafts = async () => {
        try {
            const res = await axios.get(`${MASTER_API}/routings/get-drafts`);
            if (res.data?.success) setPendingDrafts(res.data.drafts || []);
        } catch (err) {
            console.error('Failed to load pending routing drafts', err);
        }
    };

    // FIX: this was calling ${MASTER_API}/routings/drafts/:id (DELETE), but the
    // DELETE /drafts/:id route only exists on the routing-data-upload module
    // (routingdataupload.routes.js), not on the routings module — routings only
    // exposes GET /drafts/:id for viewing. Hitting /routings/drafts/:id with
    // DELETE therefore 404'd with Express's default "Cannot DELETE ..." page.
    const handleDeleteDraft = async (draft) => {
        const confirmed = window.confirm(`Do you want to Delete draft ${draft.routingCode} (${draft.productCode})? This cannot be undone.`);
        if (!confirmed) return;

        setDeletingDraftId(draft.routing_id);
        try {
            await axios.delete(`${MASTER_API}/routing-data-upload/drafts/${draft.routing_id}`);
            toast.success(`Draft ${draft.routingCode} deleted`);
            loadPendingDrafts();

            // Keep the Upload Routings modal's per-process draft counts/lists in
            // sync too — rowDrafts is separate state from pendingDrafts, so it
            // never picked up this deletion on its own.
            setRowDrafts(prev => {
                const next = {};
                for (const [processId, list] of Object.entries(prev)) {
                    next[processId] = list.filter(d => d.routing_id !== draft.routing_id);
                }
                return next;
            });
            setRowDraftListIds(prev => prev ? prev.filter(id => id !== draft.routing_id) : prev);
        } catch (err) {
            console.error('Failed to delete draft', err);
            toast.error(err.response?.data?.message || 'Failed to delete draft');
        } finally {
            setDeletingDraftId(null);
        }
    };

    useEffect(() => {
        loadPendingDrafts();
    }, []);

    const formatDateForInput = (dateStr) => {
        if (!dateStr) return "";
        const str = String(dateStr).trim();
        if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.substring(0, 10);
        const d = new Date(dateStr);
        if (isNaN(d)) return str.substring(0, 10);
        const year = d.getUTCFullYear();
        const month = String(d.getUTCMonth() + 1).padStart(2, "0");
        const day = String(d.getUTCDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    // ── loadBomLevel — identical to Routings.js (only used if user clicks Next/Back on an SFG) ──
    const loadBomLevel = async (productCode) => {
        try {
            const res = await axios.get(`${MASTER_API}/routings/bom-next-level/${productCode}`);
            const data = res.data || {};

            setFormHeader(prev => ({
                ...prev,
                productCode: data.header?.product_code || productCode,
                baseUnit: data.header?.base_unit || "",
                uom: data.header?.uom || "",
                alternative_bom: data.header?.alternative_bom || "",
                product_type: data.header?.product_type || ""
            }));

            const compRows = (data.children || []).map((c, index) => ({
                item: index + 1, process_id: "", sub_process_id: "",
                item_category: c.item_category || "", product_type: c.product_type || "",
                component_product: c.component_product, description: c.component_description,
                quantity: c.quantity ?? "", uom: c.uom, assembly: c.assembly ?? "",
                valid_from: c.valid_from ? formatDateForInput(c.valid_from) : "",
                valid_to: c.valid_to ? formatDateForInput(c.valid_to) : ""
            }));

            setComponents(compRows);
            setCurrentBomChildren(data.children || []);
            sfgMapRef.current[productCode] = compRows.filter(isSfgComponent).map(c => c.component_product);

            try {
                const existing = await axios.get(`${MASTER_API}/routings/by-product/${encodeURIComponent(productCode)}`);
                const ex = existing.data || {};
                if (ex.routing_id) {
                    setEditingId(ex.routing_id);
                    editingIdRef.current = ex.routing_id;
                    setOperations((ex.operations || []).length > 0 ? ex.operations.map(o => ({
                        ...o,
                        activities: o.activities || { setup_value: "", machine_value: "", labour_value: "", overhead_value: "" },
                        machine_parameters: o.machine_parameters || [],
                        process_parameters: o.process_parameters || [],
                        inspection_parameters: o.inspection_parameters || [],
                    })) : [emptyOperation()]);
                } else {
                    setEditingId(null);
                    editingIdRef.current = null;
                    setOperations([emptyOperation()]);
                }
            } catch (lookupErr) {
                setEditingId(null);
                editingIdRef.current = null;
                setOperations([emptyOperation()]);
            }
        } catch (err) {
            console.error(err);
            toast.error("Failed to load BOM level");
        }
    };

    // ── loadRoutingForViewMode — identical to Routings.js ──
    const loadRoutingForViewMode = async (productCode) => {
        try {
            const res = await axios.get(`${MASTER_API}/routings/by-product/${encodeURIComponent(productCode)}`);
            const data = res.data || {};
            const hdr = data.header || data;

            if (data.routing_id) {
                setEditingId(data.routing_id);
                editingIdRef.current = data.routing_id;
            } else {
                setEditingId(null);
                editingIdRef.current = null;
            }

            setFormHeader(prev => ({
                ...prev,
                productCode: hdr.productCode || productCode,
                baseUnit: hdr.baseUnit || "",
                uom: hdr.uom || "",
                alternative_bom: hdr.alternative_bom || "",
                product_type: hdr.product_type || ""
            }));

            const ops = data.operations || [];
            setOperations(ops.length > 0 ? ops.map(o => ({
                ...o,
                activities: o.activities || { setup_value: "", machine_value: "", labour_value: "", overhead_value: "" },
                machine_parameters: o.machine_parameters || [],
                process_parameters: o.process_parameters || [],
                inspection_parameters: o.inspection_parameters || [],
            })) : [emptyOperation()]);

            const comps = data.components || [];
            setComponents(comps.length > 0 ? comps.map((c, idx) => ({
                ...c,
                item: c.item || idx + 1,
                process_id: c.process_id || "",
                sub_process_id: c.sub_process_id || "",
                valid_from: formatDateForInput(c.valid_from),
                valid_to: formatDateForInput(c.valid_to)
            })) : []);
        } catch (err) {
            console.error("Failed to load routing for view mode:", err);
            setOperations([emptyOperation()]);
            setEditingId(null);
            editingIdRef.current = null;
            await loadBomLevel(productCode);
        }
    };

    const closeModal = () => {
        setShowModal(false);
        setIsDraftView(false);
        setRoutingCache({});
        productRoutingIdsRef.current = {};
        setBomLevelStack([]);
        visitedSfgRef.current = {};
        sfgMapRef.current = {};
        setSfgChoices([]);
        if (openedFromTreeRef.current) {
            openedFromTreeRef.current = false;
            setShowTreeModal(true);
        }
        if (openedFromUploadRef.current) {
            openedFromUploadRef.current = false;
            setShowUploadModal(true);
        }
        if (openedFromRowDraftsListRef.current) {
            openedFromRowDraftsListRef.current = false;
            setShowRowDraftsModal(true);
        }
    };

    const switchLevel = async (targetProduct) => {
        setOperations([emptyOperation()]);
        setComponents([]);
        if (isViewing) {
            await loadRoutingForViewMode(targetProduct);
        } else {
            await loadBomLevel(targetProduct);
        }
    };

    // ── openFormModal — identical to Routings.js ──
    const openFormModal = async (id, mode = 'view') => {
        setRoutingCache({});
        productRoutingIdsRef.current = {};
        isCreateModeRef.current = false;
        setBomLevelStack([]);
        visitedSfgRef.current = {};
        sfgMapRef.current = {};
        setSfgChoices([]);

        setEditingId(id);
        editingIdRef.current = id;
        setIsDraftView(mode === 'draft');
        // Drafts are unpublished/staged data — always show them read-only.
        // Publishing (turning a draft into a live routing) stays a separate,
        // deliberate action via the existing Pending Drafts flow, not an edit here.
        setIsViewing(mode === 'draft' ? true : mode === 'view');
        setRoutingLogs([]);

        try {
            const endpoint = mode === 'draft' ? `drafts/${id}` : `${id}`;
            const res = await axios.get(`${MASTER_API}/routings/${endpoint}`);
            const data = res.data || {};
            const hdr = data.header || data;

            if (hdr.productCode) {
                productRoutingIdsRef.current = { [hdr.productCode]: id };
            }

            if (mode !== 'draft') {
                try {
                    const logRes = await axios.get(`${MASTER_API}/routings/change-logs/${id}`);
                    setRoutingLogs(logRes.data || []);
                } catch (err) {
                    console.error("No change logs found or failed to fetch:", err);
                }
            }

            setFormHeader({
                productCode: hdr.productCode || "",
                mainLocation: hdr.mainLocation || "",
                startDate: hdr.startDate ? formatDateForInput(hdr.startDate) : "",
                endDate: hdr.endDate ? formatDateForInput(hdr.endDate) : "",
                baseUnit: hdr.baseUnit || "",
                uom: hdr.uom || "",
                alternative_bom: hdr.alternative_bom || "",
                reference_routing: hdr.reference_routing || "",
                product_type: (hdr.product_type || "").trim(),
            });

            setBomLevelStack([hdr.productCode || ""]);

            const ops = data.operations || [];
            setOperations(ops.length > 0 ? ops.map(o => ({
                ...o,
                id: o.id || Date.now() + Math.random(),
                activities: o.activities || { setup_value: "", setup_uom: "", machine_value: "", machine_uom: "", labour_value: "", labour_uom: "", overhead_value: "", overhead_uom: "" },
                machine_parameters: o.machine_parameters || [],
                process_parameters: o.process_parameters || [],
                inspection_parameters: o.inspection_parameters || [],
            })) : [emptyOperation()]);

            const comps = data.components || [];
            setComponents(comps.length > 0 ? comps.map((c, idx) => ({
                ...c,
                id: c.id || Date.now() + Math.random(),
                item: c.item || idx + 1,
                process_id: c.process_id || "",
                sub_process_id: c.sub_process_id || "",
                valid_from: formatDateForInput(c.valid_from),
                valid_to: formatDateForInput(c.valid_to),
            })) : []);

            setShowModal(true);
        } catch (err) {
            console.error(err);
            toast.error("Failed to load Routing details");
        }
    };

    const updateActivityValue = (opIdx, field, val) => {
        setOperations(prev => {
            const updated = [...prev];
            updated[opIdx] = { ...updated[opIdx], activities: { ...updated[opIdx].activities, [field]: val } };
            return updated;
        });
    };

    const updateParameterValue = (opIdx, type, paramIdx, val) => {
        setOperations(prev => {
            const updated = [...prev];
            const params = [...updated[opIdx][type]];
            params[paramIdx] = { ...params[paramIdx], value: val };
            updated[opIdx] = { ...updated[opIdx], [type]: params };
            return updated;
        });
    };

    const handleHeaderChange = (field, value) => setFormHeader((h) => ({ ...h, [field]: value }));

    const getWc = (wcName) => workcenters.find(w => w.workcenter_name === wcName) || {};

    const getSubProcessOptions = (processId) => {
        if (!processId) return [];
        const seen = new Map();
        workcenterConfigs
            .filter(c => c.process_id === processId && c.sub_process_id)
            .forEach(c => {
                if (!seen.has(c.sub_process_id)) {
                    seen.set(c.sub_process_id, { sub_process_id: c.sub_process_id, sub_process_name: c.sub_process_name });
                }
            });
        return Array.from(seen.values());
    };

    const uniqueProcessIds = Array.from(
        new Map(
            workcenterConfigs.filter(c => c.process_id).map(c => [c.process_id, { process_id: c.process_id, process_name: c.process_name }])
        ).values()
    );

    // ── handleSave — same as Routings.js EDIT MODE branch only (this screen never creates) ──
    const handleSave = async () => {
        if (editingId && !canEdit) {
            toast.error("You don't have Edit access. Please contact admin.");
            return;
        }
        if (!formHeader.productCode) return toast.error("Select product code");
        if (!operations || operations.length === 0) return toast.error("Add at least one operation");

        setSaving(true);
        try {
            const fd = new FormData();
            Object.keys(formHeader).forEach(k => fd.append(k, formHeader[k] || ""));
            fd.append("operations", JSON.stringify(operations));
            fd.append("components", JSON.stringify(components.map(c => ({
                ...c,
                process_id: c.process_id || null,
                sub_process_id: c.sub_process_id || null
            }))));
            documents.forEach((f) => fd.append("documents", f));

            const existingRoutingId = editingIdRef.current || null;

            if (existingRoutingId) {
                await axios.put(`${MASTER_API}/routings/update/${existingRoutingId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            } else {
                const res = await axios.post(`${MASTER_API}/routings/add`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                if (res.data?.routing_id) {
                    setEditingId(res.data.routing_id);
                    editingIdRef.current = res.data.routing_id;
                }
            }

            toast.success("Routing saved successfully!");
            setShowModal(false);
            setEditingId(null);
            editingIdRef.current = null;
            setIsViewing(false);
            fetchRoutingData();
        } catch (err) {
            console.error("[handleSave] failed:", err);
            toast.error("Failed to save routing");
        } finally {
            setSaving(false);
        }
    };

    // ── handleNextBom / goToSfg / handleBackBom — identical to Routings.js ──
    const handleNextBom = async () => {
        if (!components.length) { toast.info("No components found."); return; }
        const allSfgs = components.filter(isSfgComponent);
        if (!allSfgs.length) { toast.info("No Semi-Finished component found to route to next level."); return; }

        const currentProduct = formHeader.productCode;
        const parentProduct = currentProduct;
        const visited = visitedSfgRef.current[parentProduct] || [];
        const remaining = allSfgs.filter((c) => !visited.includes(c.component_product));

        if (!remaining.length) { toast.info("All Semi-Finished components at this level have already been routed."); return; }
        if (remaining.length === 1) { await goToSfg(parentProduct, remaining[0].component_product); return; }
        setSfgChoices(remaining);
    };

    const goToSfg = async (parentProduct, nextProduct) => {
        visitedSfgRef.current[parentProduct] = [...(visitedSfgRef.current[parentProduct] || []), nextProduct];
        setSfgChoices([]);
        setBomLevelStack((prev) => [...prev, nextProduct]);
        await switchLevel(nextProduct);
    };

    const handleBackBom = async () => {
        if (bomLevelStack.length <= 1) return;
        const newStack = [...bomLevelStack];
        newStack.pop();
        const previousProductCode = newStack[newStack.length - 1];
        setBomLevelStack(newStack);
        await switchLevel(previousProductCode);
    };

    // ── Tree — identical to Routings.js ──
    const toggleNode = (code) => setExpandedNodes((prev) => ({ ...prev, [code]: !prev[code] }));

    const openRoutingTree = async (productCode) => {
        try {
            const res = await axios.get(`${MASTER_API}/routings/tree/${productCode}`);
            setRoutingTree(res.data.data || res.data);
            setShowTreeModal(true);
        } catch (err) {
            toast.error("Failed to load Routing tree");
        }
    };

    const renderRoutingNode = (node, level = 0) => {
        if (!node) return null;
        const hasChildren = node.children && node.children.length > 0;
        const expanded = expandedNodes[node.code] ?? true;

        return (
            <div key={node.code} style={{ marginLeft: level * 25, marginTop: 6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 12px", background: level === 0 ? "#dbeafe" : "#ffffff", border: "1px solid #d1d5db", borderRadius: 6, boxShadow: "0 2px 5px rgba(0,0,0,0.08)" }}>
                    {hasChildren ? (
                        <button onClick={() => toggleNode(node.code)} style={{ width: 20, height: 20, fontSize: 12, border: "none", borderRadius: "50%", background: "#2563eb", color: "white", cursor: "pointer" }}>
                            {expanded ? "-" : "+"}
                        </button>
                    ) : (
                        <span style={{ fontSize: 20 }}>•</span>
                    )}
                    <div style={{ flex: 1 }}>
                        <div
                            style={{ fontWeight: 600, fontSize: 13, color: node.routing_id ? "#2563eb" : undefined, cursor: node.routing_id ? "pointer" : "default", textDecoration: node.routing_id ? "underline" : "none", width: "fit-content" }}
                            title={node.routing_id ? "View Routing for this product" : "No Routing found for this product"}
                            onClick={() => {
                                if (!node.routing_id) { toast.error("No Routing found for this product"); return; }
                                openedFromTreeRef.current = true;
                                setShowTreeModal(false);
                                openFormModal(node.routing_id, "view");
                            }}
                        >
                            {node.code}
                        </div>
                        <div style={{ fontSize: 10, color: "#666" }}>{node.description || "No Description"}</div>
                        {(node.quantity || node.base_unit || node.uom) && (
                            <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}>
                                {node.quantity !== undefined && node.quantity !== null && node.quantity !== "" && (<>Qty: <strong style={{ color: "#334155" }}>{node.quantity}</strong></>)}
                                {node.base_unit && (<>{node.quantity ? "  •  " : ""}Base Unit: <strong style={{ color: "#334155" }}>{node.base_unit}</strong></>)}
                                {node.uom && (<>{(node.quantity || node.base_unit) ? "  •  " : ""}UOM: <strong style={{ color: "#334155" }}>{node.uom}</strong></>)}
                            </div>
                        )}
                    </div>
                    {hasChildren && (<span style={{ background: "#e0f2fe", padding: "2px 8px", borderRadius: 20, fontSize: 10 }}>{node.children.length} items</span>)}
                </div>
                {expanded && hasChildren && node.children.map((child) => renderRoutingNode(child, level + 1))}
            </div>
        );
    };

    // ── Delete — identical to Routings.js ──
    const handleDeleteRouting = async (routingCode) => {
        if (!canDelete) { toast.error("You don't have Delete access. Please contact admin."); return; }
        if (!routingCode) { toast.error("Missing routing code, cannot delete."); return; }
        const confirmed = window.confirm(`Delete routing ${routingCode}? This will hide it from the list.`);
        if (!confirmed) return;

        try {
            await axios.delete(`${MASTER_API}/routings/by-code/${encodeURIComponent(routingCode)}`);
            toast.success(`Routing ${routingCode} deleted.`);
            fetchRoutingData();
        } catch (err) {
            console.error("Failed to delete routing:", err);
            toast.error("Failed to delete routing. Please try again.");
        }
    };

    // ── Upload-screen filter/table logic (unchanged) ──
    const fetchFilterOptions = async () => {
        try {
            const res = await axios.get(`${MASTER_API}/product-master/filter-options`, {
                params: {
                    productType: filters.productType?.value || '', category: filters.category?.value || '',
                    cableType: filters.cableType?.value || '', code: filters.code || '', color: filters.color || '',
                    noOfCores: filters.cores?.value || '', nominalCrossSection: filters.crossSection?.value || '',
                    referenceOfStandard: filters.referenceOfStandard?.value || '', voltage: filters.voltage?.value || ''
                }
            });
            setFilterOptions(prev => ({ ...prev, ...res.data }));
        } catch (e) {
            console.error('Failed to fetch filter options', e);
        }
    };

    useEffect(() => {
        if (filters.productType?.value) fetchFilterOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.productType, filters.category, filters.cableType, filters.code, filters.color, filters.cores, filters.crossSection, filters.referenceOfStandard, filters.voltage]);

    useEffect(() => {
        const loadCodeDescList = async () => {
            try {
                const res = await axios.get(`${MASTER_API}/product-master/productCodeDescriptionList`, { params: { productType: filters.productType?.value || '' } });
                setCodeDescList(res.data || []);
            } catch (e) {
                console.error('Failed to load product code/description list', e);
            }
        };
        if (filters.productType?.value) loadCodeDescList(); else setCodeDescList([]);
    }, [filters.productType]);

    const fetchRoutingData = async () => {
        if (!filters.productType?.value) { setRows([]); setTotalRows(0); return; }
        try {
            const res = await axios.get(`${MASTER_API}/routing-data-upload/upload-list`, {
                params: {
                    page: currentPage, limit: itemsPerPage, productType: filters.productType?.value || '',
                    category: filters.category?.value || '', cableType: filters.cableType?.value || '',
                    code: filters.code || '', color: filters.color || '', noOfCores: filters.cores?.value || '',
                    nominalCrossSection: filters.crossSection?.value || '', referenceOfStandard: filters.referenceOfStandard?.value || '',
                    voltage: filters.voltage?.value || '', construction: filters.construction?.value || '',
                    productCode: filters.productCode || '', description: filters.description || ''
                }
            });
            setRows(res.data?.data || []);
            setTotalRows(res.data?.total || 0);
            setTotalPages(Math.max(1, Math.ceil((res.data?.total || 0) / itemsPerPage)));
        } catch (e) {
            console.error('Failed to fetch routing data', e);
        }
    };

    useEffect(() => {
        fetchRoutingData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, currentPage]);

    useEffect(() => {
        setCurrentPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const activeFilterCount = [
        filters.productType, filters.category, filters.cableType, filters.code, filters.color,
        filters.cores, filters.crossSection, filters.referenceOfStandard, filters.voltage,
        filters.construction, filters.productCode, filters.description
    ].filter(v => v !== null && v !== '' && v !== undefined).length;

    const updateFilter = (patch) => setFilters(prev => ({ ...prev, ...patch }));

    const clearAll = () => setFilters({
        productType: null, category: null, cableType: null, code: '', color: '',
        cores: null, crossSection: null, referenceOfStandard: null, voltage: null,
        construction: null, productCode: '', description: ''
    });

    // ── Process-wise Upload modal: fetch the list of processes to show as rows ──
    const fetchUploadProcesses = async () => {
        setLoadingUploadProcesses(true);
        try {
            // Expected response: [{ process_id: '8101', process_name: 'RBD' }, ...]
            const res = await axios.get(`${MASTER_API}/routing-data-upload/processes`, {
                params: { productType: filters.productType?.value || '' }
            });
            const data = Array.isArray(res.data) ? res.data : Array.isArray(res.data?.data) ? res.data.data : [];
            setUploadProcesses(data);
        } catch (err) {
            console.error('Failed to fetch process list for upload', err);
            // Fallback: reuse whatever processes are already known from workcenter config
            setUploadProcesses(uniqueProcessIds);
        } finally {
            setLoadingUploadProcesses(false);
        }
    };

    const openUploadModal = () => {
        if (!canCreate) { toast.error("You don't have Create access. Please contact admin."); return; }
        setRowFiles({});
        setRowStatus({});
        setRowDownloading({});
        setRowDrafts({});
        setRowErrorMsg({});
        setShowUploadModal(true);
        fetchUploadProcesses();
    };

    const closeUploadModal = () => {
        setShowUploadModal(false);
        setUploadProcesses([]);
        setRowFiles({});
        setRowStatus({});
        setRowDownloading({});
        setRowDrafts({});
        setRowErrorMsg({});
    };

    // ── Download the Excel template for a single process ──
    const handleDownloadTemplate = async (proc) => {
        setRowDownloading(prev => ({ ...prev, [proc.process_id]: true }));
        try {
            const res = await axios.get(`${MASTER_API}/routing-data-upload/template/${proc.process_id}`, {
                params: { productType: filters.productType?.value || '' },
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${proc.process_id}_${proc.process_name || 'template'}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Failed to download template', err);
            toast.error(`Failed to download template for ${proc.process_id}`);
        } finally {
            setRowDownloading(prev => ({ ...prev, [proc.process_id]: false }));
        }
    };

    // ── Remove a staged file before uploading (lets the user pick a different one) ──
    const handleRowClearFile = (proc) => {
        setRowFiles(prev => { const next = { ...prev }; delete next[proc.process_id]; return next; });
        setRowStatus(prev => { const next = { ...prev }; delete next[proc.process_id]; return next; });
    };

    // ── File picked -> stage it into the draft tables immediately ──
    const handleRowFileSelect = async (proc, e) => {
        const file = e.target.files[0];
        e.target.value = null; // allow re-selecting the same file later
        if (!file) return;
        setRowFiles(prev => ({ ...prev, [proc.process_id]: file }));
        await stageRowFile(proc, file);
    };

        // ── Parses + stages the file into draft tables (was the old "Upload" click) ──
    const stageRowFile = async (proc, file, confirmedReplacements = []) => {
        const uploadData = new FormData();
        uploadData.append('file', file);
        uploadData.append('productType', filters.productType?.value || '');
        uploadData.append('processId', proc.process_id);
        if (confirmedReplacements.length) {
            uploadData.append('confirmedReplacements', JSON.stringify(confirmedReplacements));
        }

        setRowStatus(prev => ({ ...prev, [proc.process_id]: 'staging' }));
        setRowErrorMsg(prev => ({ ...prev, [proc.process_id]: '' }));
        try {
            const res = await axios.post(`${MASTER_API}/routing-data-upload/upload-by-process`, uploadData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            // Check needsReplaceConfirmation BEFORE the success===false check —
            // a product awaiting replace-confirmation is not a hard failure,
            // and must not be short-circuited by the generic error branch below.
            if (res.data?.needsReplaceConfirmation?.length) {
                setReplaceConfirm({ proc, file, items: res.data.needsReplaceConfirmation });
                if (res.data?.duplicates?.length) {
                    toast.warning(`Routing already exists for: ${res.data.duplicates.join(', ')} — skipped.`);
                }
                setRowStatus(prev => ({ ...prev, [proc.process_id]: 'idle' }));
                return;
            }

            if (res.data?.success === false) {
                setRowStatus(prev => ({ ...prev, [proc.process_id]: 'error' }));
                setRowErrorMsg(prev => ({ ...prev, [proc.process_id]: res.data.message || 'Staging failed' }));
                toast.error(res.data.message || `Failed to stage file for ${proc.process_id}`);
                return;
            }

            const hasDrafts = (res.data?.drafts || []).length > 0;
            setRowStatus(prev => ({
                ...prev,
                [proc.process_id]: hasDrafts ? 'staged' : (res.data?.needsReplaceConfirmation?.length ? 'idle' : prev[proc.process_id])
            }));
            setRowDrafts(prev => ({ ...prev, [proc.process_id]: res.data?.drafts || [] }));
            loadPendingDrafts();

            if (hasDrafts) {
                toast.success(res.data?.message || `${proc.process_name || proc.process_id} staged — review with View, then Upload to publish`);
            }
            if (res.data?.duplicates?.length) {
                toast.warning(`Routing already exists for: ${res.data.duplicates.join(', ')} — skipped.`);
            }
        } catch (err) {
            console.error(`Failed to stage routing for process ${proc.process_id}`, err);
            setRowStatus(prev => ({ ...prev, [proc.process_id]: 'error' }));
            setRowErrorMsg(prev => ({ ...prev, [proc.process_id]: err.response?.data?.message || 'Staging failed' }));
            toast.error(err.response?.data?.message || `Failed to stage file for ${proc.process_id}`);
        }
    };

    // ── Publishes every draft staged for this row into the LIVE routing tables ──
    const handlePublishRow = async (proc) => {
        const drafts = rowDrafts[proc.process_id] || [];
        if (!drafts.length) { toast.warning('Nothing staged to publish yet — browse a file first'); return; }

        setRowPublishing(prev => ({ ...prev, [proc.process_id]: true }));
        try {
            for (const d of drafts) {
                await axios.post(`${MASTER_API}/routings/publish-draft`, { draft_routing_id: d.routing_id });
            }
            setRowStatus(prev => ({ ...prev, [proc.process_id]: 'published' }));
            toast.success(`${proc.process_name || proc.process_id} published to the live routing`);
            fetchRoutingData();
            loadPendingDrafts();
        } catch (err) {
            console.error(`Failed to publish routing for process ${proc.process_id}`, err);
            toast.error(err.response?.data?.message || `Failed to publish ${proc.process_id}`);
        } finally {
            setRowPublishing(prev => ({ ...prev, [proc.process_id]: false }));
        }
    };

    return (
        <Layout>
            <div style={styles.pageTitleBar}>
                <div>
                    <h2 style={styles.pageTitle}>Routing Data Upload</h2>
                    <p style={styles.pageSubtitle}>Upload routing data by product type</p>
                </div>
                <div>
                    <button
                        type="button"
                        onClick={openUploadModal}
                        style={{ width: '200px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontWeight: 600, borderRadius: '6px', whiteSpace: 'nowrap', background: '#021843', color: '#fff', border: '1px solid #021843', cursor: 'pointer' }}
                    >
                        Upload Routing File
                    </button>
                </div>
            </div>

            <div style={styles.filterCard}>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12 }}>
                    <div style={{ minWidth: 220 }}>
                        <label style={styles.compactLabel}>Product Type</label>
                        <Select
                            styles={filterSelectStyles}
                            options={productTypeOptions}
                            value={filters.productType}
                            placeholder="Select Product Type"
                            onChange={(v) => setFilters({
                                productType: v, category: null, cableType: null, code: '', color: '',
                                cores: null, crossSection: null, referenceOfStandard: null, voltage: null,
                                construction: null, productCode: '', description: ''
                            })}
                            isClearable
                        />
                    </div>
                    {activeFilterCount > 0 && (
                        <button type="button" className="btn btn-outline-secondary btn-sm" style={{ fontSize: 12, height: 38, whiteSpace: 'nowrap' }} onClick={clearAll}>
                            &times; Clear All ({activeFilterCount})
                        </button>
                    )}
                </div>

                {hasType && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 15 }}>
                        <div style={fieldBox}><label style={styles.compactLabel}>Category</label>
                            <Select styles={filterSelectStyles} options={toStrOptions(filterOptions.category)} value={filters.category} placeholder="Category"
                                onChange={(v) => updateFilter({ category: v, cableType: null, code: '', color: '', cores: null, crossSection: null, referenceOfStandard: null, voltage: null, construction: null })} isClearable /></div>
                        <div style={fieldBox}><label style={styles.compactLabel}>Cable Type</label>
                            <Select styles={filterSelectStyles} options={toStrOptions(filterOptions.cableType)} value={filters.cableType} placeholder="Cable Type"
                                onChange={(v) => updateFilter({ cableType: v, code: '', color: '', cores: null, crossSection: null, referenceOfStandard: null, voltage: null, construction: null })} isClearable /></div>
                        <div style={fieldBox}><label style={styles.compactLabel}>Code</label>
                            <Select styles={filterSelectStyles} options={toStrOptions(filterOptions.code)} value={filters.code ? { label: filters.code, value: filters.code } : null} placeholder="Code"
                                onChange={(v) => updateFilter({ code: v ? v.value : '', color: '', cores: null, crossSection: null, referenceOfStandard: null, voltage: null, construction: null })} isClearable /></div>
                        <div style={fieldBox}><label style={styles.compactLabel}>Color</label>
                            <Select styles={filterSelectStyles} options={toStrOptions(filterOptions.color)} value={filters.color ? { label: filters.color, value: filters.color } : null} placeholder="Color"
                                onChange={(v) => updateFilter({ color: v ? v.value : '', cores: null, crossSection: null, referenceOfStandard: null, voltage: null, construction: null })} isClearable /></div>
                        <div style={fieldBox}><label style={styles.compactLabel}>Cores</label>
                            <Select styles={filterSelectStyles} options={toStrOptions(filterOptions.noOfCores)} value={filters.cores} placeholder="Cores"
                                onChange={(v) => updateFilter({ cores: v, crossSection: null, referenceOfStandard: null, voltage: null, construction: null })} isClearable /></div>
                        <div style={fieldBox}><label style={styles.compactLabel}>Cross Section</label>
                            <Select styles={filterSelectStyles} options={toStrOptions(filterOptions.nominalCrossSection)} value={filters.crossSection} placeholder="Cross Section"
                                onChange={(v) => updateFilter({ crossSection: v, referenceOfStandard: null, voltage: null, construction: null })} isClearable /></div>
                        <div style={fieldBox}><label style={styles.compactLabel}>Std. Ref</label>
                            <Select styles={filterSelectStyles} options={toStrOptions(filterOptions.referenceOfStandard)} value={filters.referenceOfStandard} placeholder="Std. Ref"
                                onChange={(v) => updateFilter({ referenceOfStandard: v, voltage: null, construction: null })} isClearable /></div>
                        <div style={fieldBox}><label style={styles.compactLabel}>Voltage</label>
                            <Select styles={filterSelectStyles} options={toStrOptions(filterOptions.voltage)} value={filters.voltage} placeholder="Voltage"
                                onChange={(v) => updateFilter({ voltage: v, construction: null })} isClearable /></div>
                        <div style={fieldBox}><label style={styles.compactLabel}>Construction</label>
                            <Select styles={filterSelectStyles} options={toStrOptions(filterOptions.construction)} value={filters.construction} placeholder="Construction"
                                onChange={(v) => updateFilter({ construction: v })} isClearable /></div>
                    </div>
                )}

                {hasType && (
                    <div style={{ display: 'flex', gap: 15, marginTop: 15 }}>
                        <div style={{ minWidth: 220 }}>
                            <label style={styles.compactLabel}>Product Code</label>
                            <Select
                                styles={filterSelectStyles}
                                options={codeDescList.map(item => ({ value: item.productCode, label: item.productCode }))}
                                value={filters.productCode ? { label: filters.productCode, value: filters.productCode } : null}
                                placeholder="Product Code..."
                                onChange={(v) => {
                                    const match = codeDescList.find(item => item.productCode === (v ? v.value : ''));
                                    updateFilter({ productCode: v ? v.value : '', description: match ? (match.description || '') : '' });
                                }}
                                isClearable
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <label style={styles.compactLabel}>Product Description</label>
                            <Select
                                styles={filterSelectStyles}
                                options={codeDescList.filter(item => item.description).map(item => ({ value: item.description, label: item.description }))}
                                value={filters.description ? { label: filters.description, value: filters.description } : null}
                                placeholder="Product Description..."
                                onChange={(v) => {
                                    const match = codeDescList.find(item => item.description === (v ? v.value : ''));
                                    updateFilter({ description: v ? v.value : '', productCode: match ? (match.productCode || '') : '' });
                                }}
                                isClearable
                            />
                        </div>
                    </div>
                )}

                {hasType && (<div style={{ marginTop: 8, fontSize: 13, color: '#16a34a', fontWeight: 600 }}>{totalRows} records match</div>)}
            </div>

            {hasType && (
                <div style={styles.tableContainer}>
                    <table className="table" style={styles.erpTable}>
                        <thead>
                            <tr>
                                <th style={styles.erpTh}>Routing Id</th>
                                <th style={styles.erpTh}>Product Code</th>
                                <th style={styles.erpTh}>Description</th>
                                <th style={styles.erpTh}>Base Unit</th>
                                <th style={styles.erpTh}>UOM</th>
                                <th style={styles.erpTh}>Alternative BOM</th>
                                <th style={styles.erpTh}>Location</th>
                                <th style={styles.erpTh}>Process</th>
                                <th style={styles.erpTh}>Process Name</th>
                                <th style={styles.erpTh}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length > 0 ? rows.map((r, i) => (
                                <tr key={r.routingId || i}>
                                    <td style={styles.erpTd}>{r.routingId || '-'}</td>
                                    <td style={styles.erpTd}>{r.productCode || '-'}</td>
                                    <td style={styles.erpTd}>{r.description || '-'}</td>
                                    <td style={styles.erpTd}>{r.baseUnit || '-'}</td>
                                    <td style={styles.erpTd}>{r.uom || '-'}</td>
                                    <td style={styles.erpTd}>{r.alternativeBOM || '-'}</td>
                                    <td style={styles.erpTd}>{r.location || '-'}</td>
                                    <td style={styles.erpTd}>{r.process || '-'}</td>
                                    <td style={styles.erpTd}>{r.processName || '-'}</td>
                                    <td style={styles.erpTd}>
                                        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                            <button
                                                className="btn btn-outline-dark btn-sm"
                                                style={{ fontSize: "11px", padding: "2px 8px" }}
                                                onClick={() => {
                                                    if (!canView) { toast.error("You don't have View access."); return; }
                                                    openFormModal(r.sortKey, "view");
                                                }}
                                            >
                                                View
                                            </button>
                                            <button className="btn btn-outline-success btn-sm" style={{ fontSize: "11px", padding: "2px 8px" }} onClick={() => openRoutingTree(r.productCode)}>
                                                Tree
                                            </button>
                                            <button
                                                title="Delete"
                                                style={{ background: "#fff", border: "1px solid #f5c2c7", borderRadius: "4px", display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "4px 8px", cursor: "pointer" }}
                                                onClick={() => handleDeleteRouting(r.routingId)}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 16 16" fill="#dc3545" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6Z" />
                                                    <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1ZM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118ZM2.5 3V2h11v1h-11Z" />
                                                </svg>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            )) : (
                                <tr><td colSpan={10} className="text-center text-muted p-3">No routing data found</td></tr>
                            )}
                        </tbody>
                    </table>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '10px', marginTop: '15px', width: '100%' }}>
                        <button className="btn btn-light btn-sm" disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}>Previous</button>
                        <span style={{ fontSize: 13 }}>Page {currentPage} of {totalPages}</span>
                        <button className="btn btn-light btn-sm" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}>Next</button>
                    </div>
                </div>
            )}

            {/* ===================== UPLOAD ROUTING FILE (process-wise) MODAL ===================== */}
            {showUploadModal && ReactDOM.createPortal(
                <>
                    <div onClick={closeUploadModal} style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.5)", zIndex: 10004 }} />
                    <div onClick={(e) => e.stopPropagation()} style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: "min(1200px, 95vw)", maxHeight: "88vh", display: "flex", flexDirection: "column", background: "white", zIndex: 10005, borderRadius: 12, padding: 20, boxShadow: "0 0 20px rgba(0,0,0,0.3)" }}>

                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                            <div>
                                <h3 style={{ margin: 0 }}>Upload Routings</h3>
                                <p style={{ margin: "4px 0 0 0", fontSize: 12, color: "#64748b" }}>
                                    {filters.productType?.label && (
                                        <>Product Type: <strong>{filters.productType.label}</strong> — </>
                                    )}
                                    Download the template for a process, fill it in, then upload it back for that same process.
                                </p>
                            </div>
                            <button onClick={closeUploadModal} style={{ background: "#bbb", border: "none", padding: "6px 12px", borderRadius: 6, cursor: "pointer" }}>Close</button>
                        </div>
                        <hr style={{ margin: "12px 0" }} />

                        <div style={{ flex: 1, overflowY: "auto" }}>
                            {loadingUploadProcesses ? (
                                <div style={{ textAlign: "center", color: "#64748b", padding: "30px 0" }}>Loading processes…</div>
                            ) : uploadProcesses.length === 0 ? (
                                <div style={{ textAlign: "center", color: "#94a3b8", padding: "30px 0" }}>No processes found for this product type.</div>
                            ) : (
                                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                    <thead>
                                        <tr style={{ background: "#f8fafc" }}>
                                            <th style={thStyle}>Process ID</th>
                                            <th style={thStyle}>Process Name</th>
                                            <th style={{ ...thStyle, textAlign: "center" }}>Template</th>
                                            <th style={{ ...thStyle, textAlign: "center" }}>Browse File</th>
                                            <th style={thStyle}>Status</th>
                                            <th style={thStyle}>View</th>
                                            <th style={{ ...thStyle, textAlign: "center" }}>Upload</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {uploadProcesses.map((proc) => {
                                            const fileName = rowFiles[proc.process_id]?.name;
                                            const isDownloading = !!rowDownloading[proc.process_id];
                                            const status = rowStatus[proc.process_id]; // 'staging' | 'staged' | 'error' | 'published'
                                            const isStaging = status === 'staging';
                                            const isPublishing = !!rowPublishing[proc.process_id];
                                            const drafts = rowDrafts[proc.process_id] || [];
                                            const errorMsg = rowErrorMsg[proc.process_id];
                                            const inputId = `upload-file-${proc.process_id}`;
                                            const canPublish = drafts.length > 0 && status !== 'published';

                                            return (
                                                <tr key={proc.process_id}>
                                                    <td style={tdStyle}>{proc.process_id}</td>
                                                    <td style={tdStyle}>{proc.process_name}</td>
                                                    <td style={{ ...tdStyle, textAlign: "center" }}>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleDownloadTemplate(proc)}
                                                            disabled={isDownloading}
                                                            style={{ fontSize: 11, padding: "5px 10px", borderRadius: 6, border: "1px solid #021843", background: "#fff", color: "#021843", cursor: isDownloading ? "wait" : "pointer", fontWeight: 600 }}
                                                        >
                                                            {isDownloading ? "Downloading…" : "Download Template"}
                                                        </button>
                                                    </td>
                                                    <td style={{ ...tdStyle, textAlign: "center" }}>
                                                        <input
                                                            type="file"
                                                            id={inputId}
                                                            accept=".xlsx,.xls,.csv"
                                                            style={{ display: "none" }}
                                                            onChange={(e) => handleRowFileSelect(proc, e)}
                                                        />
                                                        <label
                                                            htmlFor={inputId}
                                                            style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, padding: "5px 10px", borderRadius: 6, border: "1px solid #021843", background: isStaging ? "#94a3b8" : "#fff", color: isStaging ? "#fff" : "#021843", cursor: isStaging ? "wait" : "pointer", fontWeight: 600, pointerEvents: isStaging ? "none" : "auto" }}
                                                        >
                                                            {isStaging ? "Staging…" : "Browse File"}
                                                        </label>
                                                    </td>
                                                    <td style={tdStyle}>
                                                        {isStaging ? (
                                                            <span style={{ color: "#1976d2" }}>Staging…</span>
                                                        ) : status === 'staged' ? (
                                                            <span style={{ color: "#16a34a" }}>Staged ✓ — {drafts.length} product{drafts.length === 1 ? "" : "s"}</span>
                                                        ) : status === 'published' ? (
                                                            <span style={{ color: "#0f766e", fontWeight: 700 }}>Published ✓</span>
                                                        ) : status === 'error' ? (
                                                            <span style={{ color: "#dc2626" }} title={errorMsg}>Failed{errorMsg ? ` — ${errorMsg}` : ""}</span>
                                                        ) : fileName ? (
                                                            <span style={{ color: "#334155" }}>{fileName}</span>
                                                        ) : (
                                                            <span style={{ color: "#94a3b8" }}>No file chosen</span>
                                                        )}
                                                    </td>
                                                    <td style={tdStyle}>
                                                        {drafts.length === 0 ? (
                                                            <span style={{ color: "#cbd5e1" }}>—</span>
                                                        ) : drafts.length === 1 ? (
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    openedFromUploadRef.current = true;
                                                                    setShowUploadModal(false);
                                                                    openFormModal(drafts[0].routing_id, 'draft');
                                                                }}
                                                                style={{ fontSize: 11, padding: "3px 10px", borderRadius: 6, border: "1px solid #1976d2", background: "#eff6ff", color: "#1976d2", cursor: "pointer", fontWeight: 600 }}
                                                            >
                                                                View {drafts[0].productCode}
                                                            </button>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    setRowDraftListIds(drafts.map(d => d.routing_id));
                                                                    openedFromUploadRef.current = true;
                                                                    setShowUploadModal(false);
                                                                    setShowRowDraftsModal(true);
                                                                }}
                                                                style={{ fontSize: 11, padding: "3px 10px", borderRadius: 6, border: "1px solid #1976d2", background: "#eff6ff", color: "#1976d2", cursor: "pointer", fontWeight: 600 }}
                                                            >
                                                                View ({drafts.length} products)
                                                            </button>
                                                        )}
                                                    </td>
                                                    <td style={{ ...tdStyle, textAlign: "center" }}>
                                                        <button
                                                            type="button"
                                                            onClick={() => handlePublishRow(proc)}
                                                            disabled={!canPublish || isPublishing}
                                                            style={{
                                                                fontSize: 11, padding: "5px 14px", borderRadius: 6, border: "none",
                                                                background: (!canPublish || isPublishing) ? "#94a3b8" : "#10b981",
                                                                color: "#fff",
                                                                cursor: (!canPublish || isPublishing) ? "not-allowed" : "pointer",
                                                                fontWeight: 600
                                                            }}
                                                        >
                                                            {isPublishing ? "Uploading…" : status === 'published' ? "Uploaded ✓" : "Upload"}
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 15, paddingTop: 15, borderTop: "1px solid #eee" }}>
                            <button onClick={closeUploadModal} style={{ padding: "8px 20px", background: "#e2e8f0", color: "#1e293b", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>Close</button>
                        </div>

                    </div>
                </>,
                document.body
            )}

            {/* ===================== MAIN VIEW / EDIT MODAL — identical to Routings.js ===================== */}
            {showModal && ReactDOM.createPortal(
                <>
                    <div onClick={closeModal} style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.5)", zIndex: 10012 }} />
                    <div onClick={(e) => e.stopPropagation()} style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: "95vw", height: "95vh", overflowY: "hidden", display: "flex", flexDirection: "column", background: "white", zIndex: 10013, borderRadius: 12, padding: 20, boxShadow: "0 0 20px rgba(0,0,0,0.3)", fontSize: "12px" }}>

                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: 10 }}>
                                {isDraftView ? "Draft Preview" : editingId ? (isViewing ? "View Routing" : "Edit Routing") : "Routing"}
                                {isDraftView && (
                                    <span style={{ fontSize: 11, fontWeight: 700, color: "#b45309", background: "#fef3c7", padding: "3px 10px", borderRadius: 20 }}>
                                        UNPUBLISHED — staged from upload
                                    </span>
                                )}
                            </h3>
                            <div style={{ display: "flex", gap: 8 }}>
                                {isViewing && !isDraftView && (
                                    <button
                                        onClick={() => {
                                            if (!canEdit) { toast.error("You don't have Edit access. Please contact admin."); return; }
                                            setIsViewing(false);
                                        }}
                                        style={{ padding: "6px 14px", background: "#1976d2", color: "white", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}
                                    >
                                        Edit
                                    </button>
                                )}
                                <button onClick={closeModal} style={{ background: "#bbb", border: "none", padding: "6px 12px", borderRadius: 6, cursor: "pointer" }}>Close</button>
                            </div>
                        </div>
                        <hr style={{ margin: "12px 0" }} />
                        <div style={{ flex: 1, overflowY: "auto", marginTop: 15, paddingRight: 5 }}>
                            {/* HEADER FIELDS */}
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, flexShrink: 0 }}>
                                <div style={{ minWidth: "200px" }}>
                                    <label style={smallLabel}>Product Code</label>
                                    <Select
                                        styles={customSelectStyles}
                                        options={products.map(p => {
                                            const code = p.productCode || p.productcode;
                                            const desc = p.productDescription || "";
                                            return { value: code, label: desc ? `${code} - ${desc}` : code };
                                        })}
                                        value={formHeader.productCode ? {
                                            value: formHeader.productCode,
                                            label: (() => {
                                                const p = products.find(x => String(x.productCode || x.productcode) === String(formHeader.productCode));
                                                const desc = p?.productDescription || "";
                                                return desc ? `${formHeader.productCode} - ${desc}` : formHeader.productCode;
                                            })()
                                        } : null}
                                        isDisabled={true}
                                        placeholder="Select"
                                        isClearable
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                    />
                                </div>
                                <div><label style={smallLabel}>Base Unit</label><input style={smallInput} value={formHeader.baseUnit || ""} onChange={(e) => handleHeaderChange("baseUnit", e.target.value)} readOnly={isViewing} /></div>
                                <div><label style={smallLabel}>UOM</label><input style={{ ...smallInput, background: "#f5f5f5" }} value={formHeader.uom || ""} readOnly /></div>
                                <div>
                                    <label style={smallLabel}>Alternative BOM</label>
                                    <Select
                                        styles={customSelectStyles}
                                        options={bomAlternatives.map(b => ({ value: String(b.alternative_bom ?? b), label: String(b.alternative_bom ?? b) }))}
                                        value={formHeader.alternative_bom ? { value: String(formHeader.alternative_bom), label: String(formHeader.alternative_bom) } : null}
                                        onChange={(selected) => handleHeaderChange("alternative_bom", selected ? selected.value : "")}
                                        isDisabled={isViewing}
                                        placeholder="Select"
                                        isClearable
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                    />
                                </div>
                                <div>
                                    <label style={smallLabel}>Location</label>
                                    <Select
                                        styles={customSelectStyles}
                                        options={plants.map((pl) => ({ value: pl.name || pl.location, label: pl.name || pl.location }))}
                                        value={formHeader.mainLocation ? { value: formHeader.mainLocation, label: formHeader.mainLocation } : null}
                                        onChange={(selected) => handleHeaderChange("mainLocation", selected ? selected.value : "")}
                                        isDisabled={isViewing}
                                        placeholder="Select"
                                        isClearable
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                    />
                                </div>
                                <div><label style={smallLabel}>Valid From</label><input type="date" style={smallInput} value={formHeader.startDate} onChange={(e) => handleHeaderChange("startDate", e.target.value)} readOnly={isViewing} /></div>
                                <div><label style={smallLabel}>Valid To</label><input type="date" style={smallInput} value={formHeader.endDate} onChange={(e) => handleHeaderChange("endDate", e.target.value)} readOnly={isViewing} /></div>
                                <div>
                                    <label style={smallLabel}>Reference Routing</label>
                                    <Select
                                        styles={customSelectStyles}
                                        options={routingHeaders.map((h) => ({ value: h.routingCode, label: `${h.routingCode} - ${h.productCode}` }))}
                                        value={formHeader.reference_routing ? { value: formHeader.reference_routing, label: formHeader.reference_routing } : null}
                                        onChange={(selected) => handleHeaderChange("reference_routing", selected ? selected.value : "")}
                                        isDisabled={isViewing}
                                        placeholder="Select Reference Routing"
                                        isClearable
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                    />
                                </div>
                            </div>

                            {/* OPERATIONS SUB-HEADER */}
                            <div style={{ marginTop: 20, background: "#f8fafc", padding: 12, borderRadius: 8, border: "1px solid #e2e8f0", flexShrink: 0 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                                    <h4 style={{ margin: 0, color: "#0f172a" }}>Operations</h4>
                                </div>
                                <div style={{ overflowX: "auto" }}>
                                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                        <thead><tr style={{ background: "#e2e8f0" }}>
                                            <th style={thStyle}>Process</th><th style={thStyle}>Process Name</th><th style={thStyle}>Subprocess ID</th><th style={thStyle}>Subprocess Name</th><th style={thStyle}>Workcenter</th><th style={thStyle}>WC Desc</th>
                                        </tr></thead>
                                        <tbody>
                                            {operations.map((op, i) => (
                                                <tr key={i}>
                                                    <td style={tdStyle}>
                                                        <div style={{ minWidth: "180px" }}>
                                                            <Select
                                                                styles={customSelectStyles}
                                                                options={uniqueProcessIds.map(p => ({ value: p.process_id, label: `${p.process_id} - ${p.process_name}` }))}
                                                                value={op.process_id ? { value: op.process_id, label: `${op.process_id} - ${op.process_name}` } : null}
                                                                isDisabled={true}
                                                                placeholder="Select"
                                                                isClearable
                                                                menuPortalTarget={document.body}
                                                                menuPosition="fixed"
                                                            />
                                                        </div>
                                                    </td>
                                                    <td style={tdStyle}><input style={{ ...smallInput, background: "#f5f5f5", minWidth: "120px" }} value={op.process_name || ""} readOnly /></td>
                                                    <td style={tdStyle}>
                                                        <div style={{ minWidth: "180px" }}>
                                                            <Select
                                                                styles={customSelectStyles}
                                                                options={getSubProcessOptions(op.process_id).map(s => ({ value: s.sub_process_id, label: `${s.sub_process_id} - ${s.sub_process_name}` }))}
                                                                value={op.sub_process_id ? { value: op.sub_process_id, label: `${op.sub_process_id} - ${op.sub_process_name}` } : null}
                                                                isDisabled={true}
                                                                placeholder="Select"
                                                                isClearable
                                                                menuPortalTarget={document.body}
                                                                menuPosition="fixed"
                                                            />
                                                        </div>
                                                    </td>
                                                    <td style={tdStyle}><input style={{ ...smallInput, background: "#f5f5f5", minWidth: "120px" }} value={op.sub_process_name || ""} readOnly /></td>
                                                    <td style={tdStyle}><input style={{ ...smallInput, background: "#f5f5f5", minWidth: "140px" }} value={op.workcenterName || ""} readOnly /></td>
                                                    <td style={tdStyle}><input style={{ ...smallInput, background: "#f5f5f5", minWidth: "140px" }} value={op.workcenter_description || ""} readOnly /></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* TABS MENU */}
                            <div style={{ display: "flex", gap: 15, marginTop: 15, borderBottom: "2px solid #e2e8f0", flexShrink: 0, overflowX: "auto" }}>
                                {[
                                    { id: "components", label: "Material Details" },
                                    { id: "process_params", label: "Process Parameter" },
                                    { id: "machine_params", label: "Machine Parameter" },
                                    { id: "activities", label: "Machine Time" },
                                    { id: "inspection_params", label: "Inspection Parameter" },
                                    { id: "docs", label: "Docs" },
                                    { id: "changeLog", label: "Change Log" }
                                ].map(tab => (
                                    <button
                                        key={tab.id}
                                        onClick={() => setActiveTab(tab.id)}
                                        style={{ padding: "8px 12px", border: "none", cursor: "pointer", background: "transparent", borderBottom: activeTab === tab.id ? "3px solid #1976d2" : "3px solid transparent", fontWeight: activeTab === tab.id ? 700 : 500, whiteSpace: "nowrap" }}
                                    >
                                        {tab.label}
                                    </button>
                                ))}
                            </div>

                            {/* TABS CONTENT */}
                            {activeTab === "activities" && (() => {
                                const hasValue = (val) => val !== undefined && val !== null && val !== "";
                                const showSetup = operations.some((op) => { const wc = getWc(op.workcenterName); return (Number(wc.setup) === 1) || hasValue(op.activities?.setup_value); });
                                const showMachine = operations.some((op) => { const wc = getWc(op.workcenterName); return (Number(wc.machine) === 1) || hasValue(op.activities?.machine_value); });
                                const showLabour = operations.some((op) => { const wc = getWc(op.workcenterName); return (Number(wc.labour) === 1) || hasValue(op.activities?.labour_value); });
                                const showOverhead = operations.some((op) => { const wc = getWc(op.workcenterName); return (Number(wc.overhead) === 1) || hasValue(op.activities?.overhead_value); });
                                const hasSubprocess = operations.some(op => op.sub_process_id || op.sub_process_name);
                                const showSharedUom = showSetup || showMachine || showLabour;
                                const fixedTh = (w) => ({ ...thStyle, width: w, minWidth: w, maxWidth: w, overflow: "hidden", textOverflow: "ellipsis" });
                                const fixedTd = (w) => ({ ...tdStyle, width: w, minWidth: w, maxWidth: w, overflow: "hidden", textOverflow: "ellipsis" });

                                return (
                                    <div style={{ overflowX: "auto", marginTop: 8 }}>
                                        <table style={{ borderCollapse: "collapse", border: "1px solid #e2e8f0", tableLayout: "fixed", width: "max-content", minWidth: "100%" }}>
                                            <thead>
                                                <tr style={{ background: "#f8fafc" }}>
                                                    <th style={fixedTh(150)}>Process</th>
                                                    {hasSubprocess && <th style={fixedTh(150)}>Subprocess</th>}
                                                    <th style={fixedTh(180)}>Workcenter</th>
                                                    {showSetup && <th style={fixedTh(110)}>Setup</th>}
                                                    {showMachine && <th style={fixedTh(110)}>Run Time</th>}
                                                    {showLabour && <th style={fixedTh(110)}>Labour</th>}
                                                    {showSharedUom && <th style={fixedTh(80)}>UOM</th>}
                                                    {showOverhead && <th style={fixedTh(110)}>Overhead</th>}
                                                    {showOverhead && <th style={fixedTh(100)}>Overhead UOM</th>}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {operations.map((op, i) => {
                                                    const wc = getWc(op.workcenterName);
                                                    const inp = { ...smallInput, width: "100%" };
                                                    return (
                                                        <tr key={i}>
                                                            <td style={fixedTd(150)}>{op.process_name || op.process_id || ""}</td>
                                                            {hasSubprocess && <td style={fixedTd(150)}>{op.sub_process_name || op.sub_process_id || ""}</td>}
                                                            <td style={fixedTd(180)}>{op.workcenterName || ""}</td>
                                                            {showSetup && (<td style={fixedTd(110)}><input type="number" style={inp} value={op.activities?.setup_value || ""} disabled={!wc.setup} onChange={(e) => updateActivityValue(i, "setup_value", e.target.value)} readOnly={isViewing} /></td>)}
                                                            {showMachine && (<td style={fixedTd(110)}><input type="number" style={inp} value={op.activities?.machine_value || ""} disabled={!wc.machine} onChange={(e) => updateActivityValue(i, "machine_value", e.target.value)} readOnly={isViewing} /></td>)}
                                                            {showLabour && (<td style={fixedTd(110)}><input type="number" style={inp} value={op.activities?.labour_value || ""} disabled={!wc.labour} onChange={(e) => updateActivityValue(i, "labour_value", e.target.value)} readOnly={isViewing} /></td>)}
                                                            {showSharedUom && (<td style={fixedTd(80)}>{wc.setup_uom || wc.machine_uom || wc.labour_uom || op.activities?.setup_uom || op.activities?.machine_uom || op.activities?.labour_uom || "-"}</td>)}
                                                            {showOverhead && (<td style={fixedTd(110)}><input type="number" style={inp} value={op.activities?.overhead_value || ""} disabled={!wc.overhead} onChange={(e) => updateActivityValue(i, "overhead_value", e.target.value)} readOnly={isViewing} /></td>)}
                                                            {showOverhead && (<td style={fixedTd(100)}>{wc.overhead_uom || op.activities?.overhead_uom || "-"}</td>)}
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                );
                            })()}

                            {activeTab === "process_params" && (
                                <div style={{ marginTop: 8, overflowX: "auto" }}>
                                    {operations.every(op => (op.process_parameters || []).length === 0) ? (
                                        <div style={{ textAlign: "center", color: "#94a3b8", fontSize: 13, padding: "30px 0" }}>No process parameters found.</div>
                                    ) : (
                                        <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #e2e8f0" }}>
                                            <thead>
                                                <tr style={{ background: "#f8fafc" }}>
                                                    <th style={thStyle}>Process</th>
                                                    {operations.some(o => o.sub_process_id || o.sub_process_name) && <th style={thStyle}>Subprocess</th>}
                                                    <th style={thStyle}>Parameter</th>
                                                    <th style={thStyle}>UOM</th>
                                                    <th style={thStyle}>Specified Value</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {operations.flatMap((op, i) =>
                                                    (op.process_parameters || []).map((param, index) => (
                                                        <tr key={`${i}-${index}`} style={{ background: index % 2 === 0 ? "#fff" : "#fafbfc" }}>
                                                            <td style={tdStyle}>{op.process_name || op.process_id || ""}</td>
                                                            {operations.some(o => o.sub_process_id || o.sub_process_name) && <td style={tdStyle}>{op.sub_process_name || op.sub_process_id || ""}</td>}
                                                            <td style={{ ...tdStyle, fontWeight: 500, color: "#1e293b" }}>{param.parameter_name || ""}</td>
                                                            <td style={tdStyle}>
                                                                <input style={{ ...smallInput, width: 80 }} value={param.parameter_uom || ""} readOnly={isViewing}
                                                                    onChange={(e) => { setOperations((prev) => { const updated = [...prev]; const params = [...(updated[i].process_parameters || [])]; params[index] = { ...params[index], parameter_uom: e.target.value }; updated[i].process_parameters = params; return updated; }); }}
                                                                    placeholder="UOM" />
                                                            </td>
                                                            <td style={tdStyle}>
                                                                <input style={{ ...smallInput, width: 140 }} value={param.parameter_value || ""} readOnly={isViewing}
                                                                    onChange={(e) => { setOperations((prev) => { const updated = [...prev]; const params = [...(updated[i].process_parameters || [])]; params[index] = { ...params[index], parameter_value: e.target.value }; updated[i].process_parameters = params; return updated; }); }} />
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            )}

                            {activeTab === "machine_params" && (
                                <div style={{ marginTop: 8, overflowX: "auto" }}>
                                    {operations.every(op => (op.machine_parameters || []).length === 0) ? (
                                        <div style={{ textAlign: "center", color: "#94a3b8", fontSize: 13, padding: "30px 0" }}>No machine parameters found.</div>
                                    ) : (
                                        <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #e2e8f0" }}>
                                            <thead>
                                                <tr style={{ background: "#f8fafc" }}>
                                                    <th style={thStyle}>Process</th>
                                                    {operations.some(o => o.sub_process_id || o.sub_process_name) && <th style={thStyle}>Subprocess</th>}
                                                    <th style={thStyle}>Workcenter</th>
                                                    <th style={thStyle}>Parameter</th>
                                                    <th style={thStyle}>UOM</th>
                                                    <th style={thStyle}>Specified Value</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {operations.flatMap((op, i) =>
                                                    (op.machine_parameters || []).map((p, idx) => (
                                                        <tr key={`${i}-${idx}`} style={{ background: idx % 2 === 0 ? "#fff" : "#fafbfc" }}>
                                                            <td style={tdStyle}>{op.process_name || op.process_id || ""}</td>
                                                            {operations.some(o => o.sub_process_id || o.sub_process_name) && <td style={tdStyle}>{op.sub_process_name || op.sub_process_id || ""}</td>}
                                                            <td style={tdStyle}>{op.workcenterName || ""}</td>
                                                            <td style={{ ...tdStyle, fontWeight: 500, color: "#1e293b" }}>{p.name || ""}</td>
                                                            <td style={{ ...tdStyle, color: "#64748b" }}>{p.uom || ""}</td>
                                                            <td style={tdStyle}>
                                                                <input style={{ ...smallInput, width: 120 }} value={p.value || ""} readOnly={isViewing} onChange={(e) => updateParameterValue(i, "machine_parameters", idx, e.target.value)} />
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            )}

                            {activeTab === "components" && (
                                <div>
                                    <div style={{ width: "100%", overflowX: "auto" }}>
                                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 1100 }}>
                                            <thead>
                                                <tr style={{ background: "#f8fafc" }}>
                                                    <th style={thStyle}>Item</th>
                                                    <th style={thStyle}>Process</th>
                                                    {operations.some(o => o.sub_process_id || o.sub_process_name) && <th style={thStyle}>Sub Process</th>}
                                                    <th style={thStyle}>Product Type</th>
                                                    <th style={thStyle}>Product code</th>
                                                    <th style={thStyle}>Description</th>
                                                    <th style={thStyle}>UOM</th>
                                                    <th style={thStyle}>Qty</th>
                                                    <th style={thStyle}>Assembly</th>
                                                    <th style={thStyle}>Valid From</th>
                                                    <th style={thStyle}>Valid To</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {components.map((c, idx) => (
                                                    <tr key={idx}>
                                                        <td style={tdStyle}><input style={{ ...smallInput, width: 50 }} value={c.item} readOnly /></td>
                                                        <td style={tdStyle}>
                                                            {c.process_id
                                                                ? `${c.process_id} - ${operations.find(op => op.process_id === c.process_id)?.process_name || ''}`
                                                                : '-'}
                                                        </td>
                                                        {operations.some(o => o.sub_process_id || o.sub_process_name) && <td style={tdStyle}>{c.sub_process_id || '-'}</td>}
                                                        <td style={tdStyle}><input style={{ ...smallInput, width: 100 }} value={c.product_type || ""} readOnly /></td>
                                                        <td style={tdStyle}>{c.component_product || '-'}</td>
                                                        <td style={tdStyle}><input style={{ ...smallInput, background: "#f5f5f5", width: 120 }} value={c.description} readOnly /></td>
                                                        <td style={tdStyle}><input style={{ ...smallInput, background: "#f5f5f5", width: 50 }} value={c.uom || ""} readOnly /></td>
                                                        <td style={tdStyle}><input type="number" style={{ ...smallInput, width: 60 }} value={c.quantity ?? ""} readOnly /></td>
                                                        <td style={tdStyle}>{c.assembly || '-'}</td>
                                                        <td style={tdStyle}><input type="date" style={{ ...smallInput, width: 110 }} value={c.valid_from} readOnly /></td>
                                                        <td style={tdStyle}><input type="date" style={{ ...smallInput, width: 110 }} value={c.valid_to} readOnly /></td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {activeTab === "docs" && (
                                <div>
                                    {!isViewing && <input type="file" multiple onChange={(e) => setDocuments([...e.target.files])} style={{ padding: "6px", border: "1px solid #ccc", borderRadius: "4px" }} />}
                                    {isViewing && <p style={{ color: "#666" }}>Documents are view-only.</p>}
                                </div>
                            )}

                            {activeTab === "inspection_params" && (
                                <div style={{ marginTop: 8, overflowX: "auto" }}>
                                    {operations.every(op => (op.inspection_parameters || []).length === 0) ? (
                                        <div style={{ padding: 30, textAlign: "center", color: "#64748b", background: "#f8fafc", borderRadius: 8, border: "1px dashed #cbd5e1" }}>
                                            <h4 style={{ margin: 0 }}>Inspection Parameters</h4>
                                            <p style={{ margin: "5px 0 0 0", fontSize: 13 }}>No inspection characteristics found.</p>
                                        </div>
                                    ) : (
                                        <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #e2e8f0", fontSize: 13 }}>
                                            <thead>
                                                <tr style={{ background: "#f8fafc" }}>
                                                    <th style={thStyle}>Process</th>
                                                    {operations.some(o => o.sub_process_id || o.sub_process_name) && <th style={thStyle}>Subprocess</th>}
                                                    <th style={thStyle}>Parameter</th>
                                                    <th style={thStyle}>UOM</th>
                                                    <th style={thStyle}>Specified Value</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {operations.flatMap((op, opIdx) =>
                                                    (op.inspection_parameters || []).map((ip, ipIdx) => (
                                                        <tr key={`${opIdx}-${ipIdx}`} style={{ background: ipIdx % 2 === 0 ? "#fff" : "#fafbfc" }}>
                                                            <td style={tdStyle}>{op.process_name || op.process_id || ""}</td>
                                                            {operations.some(o => o.sub_process_id || o.sub_process_name) && <td style={tdStyle}>{op.sub_process_name || op.sub_process_id || ""}</td>}
                                                            <td style={{ ...tdStyle, fontWeight: 500, color: "#1e293b" }}>{ip.characteristic || ""}</td>
                                                            <td style={{ ...tdStyle, color: "#64748b" }}>{ip.uom || ""}</td>
                                                            <td style={tdStyle}>
                                                                <input style={{ ...smallInput, width: 140 }} value={ip.parameter_value || ""} readOnly={isViewing} placeholder="Enter value"
                                                                    onChange={(e) => { setOperations((prev) => { const updated = [...prev]; const params = [...(updated[opIdx].inspection_parameters || [])]; params[ipIdx] = { ...params[ipIdx], parameter_value: e.target.value }; updated[opIdx] = { ...updated[opIdx], inspection_parameters: params }; return updated; }); }} />
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            )}

                            {activeTab === "changeLog" && (
                                <div style={{ background: "#fff", borderRadius: 10, padding: 12, border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
                                    <div style={{ overflowX: "auto" }}>
                                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                                            <thead>
                                                <tr style={{ background: "#f8fafc" }}>
                                                    <th style={thStyle}>Date & Time</th>
                                                    <th style={thStyle}>User</th>
                                                    <th style={thStyle}>Action</th>
                                                    <th style={thStyle}>Status</th>
                                                    <th style={thStyle}>Field</th>
                                                    <th style={thStyle}>Old Value</th>
                                                    <th style={thStyle}>New Value</th>
                                                    <th style={thStyle}>Comment</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {routingLogs.length > 0 ? (
                                                    routingLogs.map((log, index) => (
                                                        <tr key={log.id || index}>
                                                            <td style={tdStyle}>{log.created_at ? new Date(log.created_at).toLocaleString() : "-"}</td>
                                                            <td style={tdStyle}>{log.created_by || "Admin"}</td>
                                                            <td style={tdStyle}>{log.action_type || "-"}</td>
                                                            <td style={tdStyle}>{log.doc_status || "-"}</td>
                                                            <td style={tdStyle}>{log.field_name || "-"}</td>
                                                            <td style={tdStyle}>{log.old_value || "-"}</td>
                                                            <td style={{ ...tdStyle, fontWeight: 700, color: "#0f172a" }}>{log.new_value || "-"}</td>
                                                            <td style={tdStyle}>{log.comment || "-"}</td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr><td colSpan="8" style={{ textAlign: "center", padding: 20, color: "#64748b" }}>No change logs available</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* SFG PICKER */}
                        {sfgChoices.length > 0 && (
                            <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000 }}>
                                <div style={{ background: "white", borderRadius: 8, padding: 20, minWidth: 320, maxWidth: 440 }}>
                                    <h4 style={{ marginTop: 0 }}>Multiple Semi-Finished Components Found</h4>
                                    <p style={{ fontSize: 13, color: "#555" }}>This product has more than one Semi-Finished (SFG) component. Select which one to route next:</p>
                                    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10, maxHeight: 280, overflowY: "auto" }}>
                                        {sfgChoices.map((c) => (
                                            <button key={c.component_product} onClick={() => goToSfg(formHeader.productCode, c.component_product)} style={{ padding: "8px 12px", textAlign: "left", border: "1px solid #ccc", borderRadius: 6, background: "#f8f9fa", cursor: "pointer" }}>
                                                <strong>{c.component_product}</strong>{c.description ? ` — ${c.description}` : ""}
                                            </button>
                                        ))}
                                    </div>
                                    <div style={{ marginTop: 14, textAlign: "right" }}>
                                        <button onClick={() => setSfgChoices([])} style={{ padding: "6px 14px", border: "none", background: "#e2e8f0", borderRadius: 6, cursor: "pointer" }}>Cancel</button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* FOOTER */}
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 15, paddingTop: 15, borderTop: "1px solid #eee", flexShrink: 0 }}>
                            {bomLevelStack.length > 1 && (
                                <button onClick={handleBackBom} style={{ padding: "8px 20px", background: "#64748b", color: "white", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>Back</button>
                            )}
                            {components.filter(isSfgComponent).length > 0 && (
                                <button onClick={handleNextBom} style={{ padding: "8px 20px", background: "#1976d2", color: "white", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>Next</button>
                            )}
                            {!isViewing && (
                                <button
                                    onClick={() => {
                                        if (editingId && !canEdit) { toast.error("You don't have Edit access.Please contact admin."); return; }
                                        handleSave();
                                    }}
                                    disabled={saving}
                                    style={{ padding: "8px 20px", background: "#1e293b", color: "white", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: "bold" }}
                                >
                                    {saving ? "Saving..." : "Save"}
                                </button>
                            )}
                        </div>
                    </div>
                </>,
                document.body
            )}

            {/* ===================== ROUTING TREE MODAL — identical to Routings.js ===================== */}
            {showTreeModal && (
                <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1200 }}>
                    <div style={{ width: "70vw", height: "75vh", padding: 14, margin: "50px auto", background: "#f8fafc", borderRadius: 12, overflowY: "auto" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Routing Tree</h3>
                                <span style={{ background: "#dbeafe", color: "#1d4ed8", padding: "4px 12px", borderRadius: 20, fontSize: 13, fontWeight: 700 }}>Product: {routingTree?.code}</span>
                            </div>
                            <button onClick={() => setShowTreeModal(false)} style={{ background: "#ef4444", color: "white", border: "none", padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>Close</button>
                        </div>
                        {routingTree && renderRoutingNode(routingTree)}
                    </div>
                </div>
            )}

            {/* ===================== FLOATING PENDING DRAFTS BADGE ===================== */}
            <div style={{ position: 'fixed', bottom: 30, right: 30, display: 'flex', flexDirection: 'column', gap: 15, zIndex: 9999 }}>
                {pendingDrafts.length > 0 && (
                    <div
                        onClick={() => setShowDraftsListModal(true)}
                        style={{ background: '#1e293b', color: '#fff', padding: '12px 20px', borderRadius: 50, cursor: 'pointer', boxShadow: '0 8px 20px rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', gap: 10 }}
                    >
                        <span style={{ fontWeight: 'bold', fontSize: 15 }}>📂 Pending Drafts</span>
                        <span style={{ background: '#ef4444', color: 'white', padding: '2px 8px', borderRadius: 20, fontSize: 12, fontWeight: 'bold' }}>
                            {pendingDrafts.length}
                        </span>
                    </div>
                )}
            </div>

            {showDraftsListModal && ReactDOM.createPortal(
                <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.5)", zIndex: 10020, display: "flex", justifyContent: "center", alignItems: "center" }}>
                    <div style={{ background: "white", width: "85vw", height: "85vh", borderRadius: 12, padding: 20, display: "flex", flexDirection: "column", boxShadow: "0 10px 30px rgba(0,0,0,0.2)" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #eee", paddingBottom: 15, marginBottom: 15 }}>
                            <h3 style={{ margin: 0, color: "#1e293b" }}>👀 Pending Routing Drafts</h3>
                            <button onClick={() => setShowDraftsListModal(false)} style={{ background: "#f1f5f9", border: "none", padding: "8px 16px", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>Close</button>
                        </div>
                        <div style={{ flex: 1, overflowY: "auto", paddingRight: 10 }}>
                            {pendingDrafts.length === 0 ? (
                                <p style={{ textAlign: 'center', marginTop: 40, color: '#666' }}>No Pending Routings found.</p>
                            ) : (
                                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                                    <thead>
                                        <tr style={{ background: "#f1f1f1" }}>
                                            <th style={thStyle}>Draft ID</th>
                                            <th style={thStyle}>Product Code</th>
                                            <th style={thStyle}>Alt BOM</th>
                                            <th style={thStyle}>Location</th>
                                            <th style={thStyle}>Operations</th>
                                            <th style={thStyle}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pendingDrafts.map((draft) => (
                                            <tr key={draft.routing_id}>
                                                <td style={tdStyle}><strong>{draft.routingCode}</strong></td>
                                                <td style={tdStyle}>{draft.productCode}</td>
                                                <td style={tdStyle}>{draft.alternative_bom || "-"}</td>
                                                <td style={tdStyle}>{draft.mainLocation || "-"}</td>
                                                <td style={tdStyle}>{draft.op_count}</td>
                                                <td style={tdStyle}>
                                                    <button
                                                        onClick={() => {
                                                            if (!canView) { toast.error("You don't have View access."); return; }
                                                            setShowDraftsListModal(false);
                                                            openFormModal(draft.routing_id, 'draft');
                                                        }}
                                                        style={{ padding: "6px 10px", background: "#1E88E5", color: "white", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 12, marginRight: 8 }}
                                                    >
                                                        View
                                                    </button>
                                                    <button
                                                        onClick={async () => {
                                                            if (!canCreate) { toast.error("You don't have Create access."); return; }
                                                            setPublishingDraftId(draft.routing_id);
                                                            try {
                                                                await axios.post(`${MASTER_API}/routings/publish-draft`, { draft_routing_id: draft.routing_id });
                                                                toast.success(`${draft.productCode} published to the live routing`);
                                                                loadPendingDrafts();
                                                                fetchRoutingData();
                                                            } catch (err) {
                                                                toast.error(err.response?.data?.message || 'Failed to publish draft');
                                                            } finally {
                                                                setPublishingDraftId(null);
                                                            }
                                                        }}
                                                        disabled={publishingDraftId === draft.routing_id}
                                                        style={{ padding: "6px 10px", background: publishingDraftId === draft.routing_id ? "#94a3b8" : "#10b981", color: "white", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 12, marginRight: 8 }}
                                                    >
                                                        {publishingDraftId === draft.routing_id ? "Publishing…" : "Upload"}
                                                    </button>
                                                    <button
                                                        title="Delete draft"
                                                        onClick={() => handleDeleteDraft(draft)}
                                                        disabled={deletingDraftId === draft.routing_id}
                                                        style={{
                                                            background: "#fff", border: "1px solid #f5c2c7", borderRadius: "4px",
                                                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                                                            padding: "6px 8px", cursor: deletingDraftId === draft.routing_id ? "wait" : "pointer"
                                                        }}
                                                    >
                                                        <svg width="14" height="14" viewBox="0 0 16 16" fill="#dc3545" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6Z" />
                                                            <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1ZM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118ZM2.5 3V2h11v1h-11Z" />
                                                        </svg>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {showRowDraftsModal && ReactDOM.createPortal(
                <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.5)", zIndex: 10006, display: "flex", justifyContent: "center", alignItems: "center" }}>
                    <div style={{ background: "white", width: "85vw", height: "85vh", borderRadius: 12, padding: 20, display: "flex", flexDirection: "column", boxShadow: "0 10px 30px rgba(0,0,0,0.2)" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #eee", paddingBottom: 15, marginBottom: 15 }}>
                            <h3 style={{ margin: 0, color: "#1e293b" }}>Staged Products for This Process</h3>
                            <button onClick={() => { setShowRowDraftsModal(false); setShowUploadModal(true); }} style={{ background: "#f1f5f9", border: "none", padding: "8px 16px", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>Close</button>
                        </div>
                        <div style={{ flex: 1, overflowY: "auto", paddingRight: 10 }}>
                            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                                <thead>
                                    <tr style={{ background: "#f1f1f1" }}>
                                        <th style={thStyle}>Draft ID</th>
                                        <th style={thStyle}>Product Code</th>
                                        <th style={thStyle}>Alt BOM</th>
                                        <th style={thStyle}>Location</th>
                                        <th style={thStyle}>Operations</th>
                                        <th style={thStyle}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pendingDrafts.filter(d => (rowDraftListIds || []).includes(d.routing_id)).map((draft) => (
                                        <tr key={draft.routing_id}>
                                            <td style={tdStyle}><strong>{draft.routingCode}</strong></td>
                                            <td style={tdStyle}>{draft.productCode}</td>
                                            <td style={tdStyle}>{draft.alternative_bom || "-"}</td>
                                            <td style={tdStyle}>{draft.mainLocation || "-"}</td>
                                            <td style={tdStyle}>{draft.op_count}</td>
                                            <td style={tdStyle}>
                                                <button
                                                    onClick={() => {
                                                        if (!canView) { toast.error("You don't have View access."); return; }
                                                        openedFromRowDraftsListRef.current = true;
                                                        setShowRowDraftsModal(false);
                                                        openFormModal(draft.routing_id, 'draft');
                                                    }}
                                                    style={{ padding: "6px 10px", background: "#1E88E5", color: "white", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 12, marginRight: 8 }}
                                                >
                                                    View
                                                </button>
                                                <button
                                                    onClick={async () => {
                                                        if (!canCreate) { toast.error("You don't have Create access."); return; }
                                                        setPublishingDraftId(draft.routing_id);
                                                        try {
                                                            await axios.post(`${MASTER_API}/routings/publish-draft`, { draft_routing_id: draft.routing_id });
                                                            toast.success(`${draft.productCode} published to the live routing`);
                                                            loadPendingDrafts();
                                                            fetchRoutingData();
                                                        } catch (err) {
                                                            toast.error(err.response?.data?.message || 'Failed to publish draft');
                                                        } finally {
                                                            setPublishingDraftId(null);
                                                        }
                                                    }}
                                                    disabled={publishingDraftId === draft.routing_id}
                                                    style={{ padding: "6px 10px", background: publishingDraftId === draft.routing_id ? "#94a3b8" : "#10b981", color: "white", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 12, marginRight: 8 }}
                                                >
                                                    {publishingDraftId === draft.routing_id ? "Publishing…" : "Upload"}
                                                </button>
                                                <button
                                                    title="Delete draft"
                                                    onClick={() => handleDeleteDraft(draft)}
                                                    disabled={deletingDraftId === draft.routing_id}
                                                    style={{
                                                        background: "#fff", border: "1px solid #f5c2c7", borderRadius: "4px",
                                                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                                                        padding: "6px 8px", cursor: deletingDraftId === draft.routing_id ? "wait" : "pointer"
                                                    }}
                                                >
                                                    <svg width="14" height="14" viewBox="0 0 16 16" fill="#dc3545" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6Z" />
                                                        <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1ZM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118ZM2.5 3V2h11v1h-11Z" />
                                                    </svg>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>,
                document.body
            )}


            {/* ===================== DELETED-ROUTING REPLACE CONFIRM MODAL ===================== */}
            {replaceConfirm && ReactDOM.createPortal(
                <div style={{
                    position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
                    background: "rgba(0,0,0,0.5)", zIndex: 10030,
                    display: "flex", justifyContent: "center", alignItems: "center"
                }}>
                    <div style={{
                        background: "#fff", width: "420px", borderRadius: 14,
                        padding: "36px 32px", boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
                        textAlign: "center", position: "relative"
                    }}>
                        <button
                            onClick={() => setReplaceConfirm(null)}
                            style={{
                                position: "absolute", top: 16, right: 18,
                                background: "transparent", border: "none",
                                fontSize: 18, cursor: "pointer", color: "#94a3b8"
                            }}
                        >
                            ✖
                        </button>

                        <h4 style={{ margin: "0 0 12px", fontSize: "19px", fontWeight: 700, color: "#1e293b" }}>
                            Routing Already Deleted
                        </h4>

                        <p style={{ fontSize: 14, color: "#64748b", marginBottom: 28, lineHeight: 1.5 }}>
                            {replaceConfirm.items.length === 1
                                ? <>Routing for product "<strong>{replaceConfirm.items[0].productCode}</strong>" already exists but is deleted. Do you want to replace it with this new data?</>
                                : <>{replaceConfirm.items.length} products in this file already have a deleted routing. Replace all of them with this new data?</>
                            }
                        </p>

                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                            <button
                                onClick={() => {
                                    const confirmedCodes = replaceConfirm.items.map(i => i.productCode);
                                    const { proc, file } = replaceConfirm;
                                    setReplaceConfirm(null);
                                    stageRowFile(proc, file, confirmedCodes);
                                }}
                                style={{
                                    padding: "12px 16px", borderRadius: 10, border: "none",
                                    background: "#16a34a", color: "#fff", cursor: "pointer",
                                    fontWeight: 600, fontSize: 14, width: "100%"
                                }}
                            >
                                Yes, Replace It
                            </button>
                            <button
                                onClick={() => setReplaceConfirm(null)}
                                style={{
                                    padding: "10px 16px", borderRadius: 10, border: "none",
                                    background: "transparent", color: "#334155", cursor: "pointer",
                                    fontWeight: 500, fontSize: 14, width: "100%"
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            <ToastContainer position="top-right" autoClose={3000} />
        </Layout>
    );
}

export default RoutingDataUpload;
